import { PasscodeCrypto } from "@/common/utils/crypto";
import type { KeyValueStorage } from "./KeyValueStorage";
import type { EncryptionKeyManager } from "./EncryptionKeyManager";

export class EncryptedKeyValueStorage implements KeyValueStorage {
	constructor(
		private readonly storage: KeyValueStorage,
		private readonly encryptionKeyManager: EncryptionKeyManager,
	) {}

	async getItem(key: string): Promise<string | null> {
		const value = await this.storage.getItem(key);
		if (!value) return null;

		const encryptionKey = await this.encryptionKeyManager.getKey();
		const decryptedValue = await PasscodeCrypto.decrypt(value, encryptionKey);
		if (!decryptedValue.ok) {
			console.error(decryptedValue.error);
			// Remove the stale entry so it doesn't keep failing.
			await this.storage.removeItem(key).catch(() => {});
			throw new Error("Failed to decrypt value");
		}
		return decryptedValue.data as string;
	}

	async getLastModified(key: string): Promise<string | null> {
		return await this.storage.getLastModified(key);
	}

	async setItem(key: string, value: string): Promise<void> {
		const encryptionKey = await this.encryptionKeyManager.getKey();
		const encryptedValue = await PasscodeCrypto.encrypt(value, encryptionKey);
		await this.storage.setItem(key, encryptedValue);
	}

	async removeItem(key: string): Promise<void> {
		await this.storage.removeItem(key);
	}

	async clear(): Promise<void> {
		await this.storage.clear();
	}

	async keys(): Promise<string[]> {
		return await this.storage.keys();
	}

	isAvailable(): boolean {
		return (
			this.storage.isAvailable() && this.encryptionKeyManager.isAvailable()
		);
	}
}
